import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Reusable SEO component — inject into any page.
 *
 * Props:
 *   title        — Page title (will be appended with site name)
 *   description  — Meta description (max ~160 chars)
 *   keywords     — Comma-separated keywords string
 *   canonical    — Canonical URL path (e.g. "/wedding")
 *   ogImage      — Absolute URL to OG image (defaults to site logo)
 *   noIndex      — Set true to prevent indexing (e.g. private pages)
 */
const SITE_NAME   = 'ASM Wedding Cards';
const SITE_URL    = 'https://www.asmweddingcards.com';
const DEFAULT_IMG = `${SITE_URL}/og-image.jpg`;
const PHONE       = '+91 9865230900';

const SEO = ({
    title,
    description,
    keywords = '',
    canonical = '',
    ogImage = DEFAULT_IMG,
    noIndex = false,
}) => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} – Premium Wedding Cards in Salem, Tamil Nadu`;
    const canonicalUrl = `${SITE_URL}${canonical}`;

    return (
        <Helmet>
            {/* ── Basic ──────────────────────────────────── */}
            <html lang="ta-IN" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="author" content="ASM Wedding Cards, Vazhapadi, Salem" />
            <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
            <link rel="canonical" href={canonicalUrl} />

            {/* ── Geo / Local SEO ────────────────────────── */}
            <meta name="geo.region" content="IN-TN" />
            <meta name="geo.placename" content="Salem, Tamil Nadu, India" />
            <meta name="geo.position" content="11.6525;78.3991" />
            <meta name="ICBM" content="11.6525, 78.3991" />
            <meta name="DC.title" content={fullTitle} />

            {/* ── Open Graph ─────────────────────────────── */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:locale" content="ta_IN" />

            {/* ── Twitter Card ───────────────────────────── */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {/* ── Business / Contact ─────────────────────── */}
            <meta name="contact" content={PHONE} />
            <meta name="category" content="Wedding Cards, Invitation Cards, Printing Services" />
            <meta name="coverage" content="Salem, Vazhapadi, Erode, Tamil Nadu, India" />
            <meta name="distribution" content="local" />
            <meta name="revisit-after" content="7 days" />
            <meta name="language" content="Tamil, English" />
            <meta name="target" content="all" />
            <meta name="HandheldFriendly" content="True" />
            <meta name="MobileOptimized" content="320" />
        </Helmet>
    );
};

/* ── Pre-built configs for every page ──────────────────────── */

export const HomeSEO = () => (
    <SEO
        title="Wedding Invitation Cards Salem, Vazhapadi | ASM Wedding Cards"
        description="ASM Wedding Cards – Best wedding invitation cards in Salem & Vazhapadi, Tamil Nadu. 2000+ designs, custom printing, puberty, housewarming & earpiercing cards. Call +91 9865230900."
        keywords="wedding cards Salem, wedding invitation cards Salem, wedding cards Vazhapadi, Tamil Nadu wedding cards, marriage invitation cards Salem, ASM wedding cards, மணவிழா அழைப்பிதழ் சேலம், wedding invitation printing Salem, best wedding cards Tamil Nadu, marriage cards Salem Tamil Nadu"
        canonical="/"
    />
);

export const WeddingSEO = () => (
    <SEO
        title="Wedding Invitation Cards Salem | Marriage Cards Vazhapadi"
        description="Beautiful wedding invitation cards in Salem, Vazhapadi & Tamil Nadu. Custom printed marriage cards with 2000+ traditional & modern designs. Fast delivery across Tamil Nadu."
        keywords="wedding invitation cards Salem, marriage cards Salem, wedding cards Vazhapadi, மணவிழா அழைப்பிதழ் சேலம், wedding invitation printing Tamil Nadu, custom wedding cards Salem, traditional wedding cards, modern marriage invitation Salem, wedding cards near me Salem"
        canonical="/wedding"
    />
);

export const PubertyCardsSEO = () => (
    <SEO
        title="Puberty Invitation Cards Salem | Manjal Neerattu Cards Tamil Nadu"
        description="Premium puberty invitation cards in Salem & Vazhapadi. Traditional manjal neerattu (half-saree) ceremony cards with custom printing. Order online or visit our Salem store."
        keywords="puberty cards Salem, manjal neerattu cards Salem, half saree ceremony cards Tamil Nadu, puberty invitation Vazhapadi, பூப்பு விழா அழைப்பிதழ் சேலம், puberty function invitation Salem, half saree function cards Salem, manjal neerattu invitation Tamil Nadu"
        canonical="/puberty"
    />
);

export const HousewarmingSEO = () => (
    <SEO
        title="Housewarming Invitation Cards Salem | Gruha Pravesam Cards Tamil Nadu"
        description="Elegant housewarming invitation cards in Salem & Tamil Nadu. Gruha Pravesam ceremony cards with premium quality printing. Custom designs for every budget."
        keywords="housewarming cards Salem, gruha pravesam cards Salem, housewarming invitation Tamil Nadu, இல்லவாழ்க்கை விழா அழைப்பிதழ் சேலம், grihapravesham cards Vazhapadi, house warming function cards Salem, new house opening invitation Tamil Nadu, housewarming ceremony cards Salem"
        canonical="/housewarming"
    />
);

export const EarpiecingSEO = () => (
    <SEO
        title="Earpiercing Ceremony Cards Salem | Karna Vedha Invitation Tamil Nadu"
        description="Beautiful earpiercing (Karna Vedha) ceremony invitation cards in Salem & Vazhapadi. Traditional and modern designs for your child's special ceremony."
        keywords="earpiercing cards Salem, karna vedha cards Salem, ear piercing invitation Tamil Nadu, காது குத்து விழா அழைப்பிதழ் சேலம், ear piercing ceremony cards Vazhapadi, earpiercing function invitation Salem, karna vedha invitation Tamil Nadu"
        canonical="/earpiercing"
    />
);

export const BirthdaySEO = () => (
    <SEO
        title="Birthday Invitation Cards Salem | Birthday Cards Tamil Nadu"
        description="Colorful birthday invitation cards in Salem & Tamil Nadu. Custom printed birthday party invitations for kids and adults. 100+ designs available."
        keywords="birthday cards Salem, birthday invitation cards Tamil Nadu, birthday cards Vazhapadi, பிறந்தநாள் அழைப்பிதழ் சேலம், birthday party invitation Salem, custom birthday cards Tamil Nadu, kids birthday invitation Salem"
        canonical="/birthday"
    />
);

export const BusinessCardsSEO = () => (
    <SEO
        title="Business Invitation Cards Salem | Corporate Cards Tamil Nadu"
        description="Professional business invitation and corporate event cards in Salem, Tamil Nadu. Premium quality printing for business events, inaugurations and corporate functions."
        keywords="business cards Salem, corporate invitation cards Tamil Nadu, inauguration cards Salem, business event invitation Tamil Nadu, company launch invitation Salem, corporate function cards Vazhapadi"
        canonical="/business"
    />
);

export const SaveTheDateSEO = () => (
    <SEO
        title="Save the Date Cards Salem | Pre-wedding Invitation Tamil Nadu"
        description="Stylish save the date cards for weddings in Salem & Tamil Nadu. Send advance wedding announcements with beautiful designs. Custom printing available."
        keywords="save the date cards Salem, pre-wedding invitation Tamil Nadu, save the date Vazhapadi, wedding announcement cards Salem, advance wedding invitation Tamil Nadu"
        canonical="/savethedate"
    />
);

export const AllCardsSEO = () => (
    <SEO
        title="All Invitation Cards Salem | Wedding Cards, Puberty & More | Tamil Nadu"
        description="Shop all invitation cards in Salem & Tamil Nadu — wedding, puberty, housewarming, earpiercing, birthday & more. 2000+ designs, fast delivery across Tamil Nadu."
        keywords="invitation cards Salem, all event cards Tamil Nadu, wedding cards Salem, puberty cards Salem, housewarming cards Tamil Nadu, ASM wedding cards, invitation printing Salem, custom cards Salem Tamil Nadu"
        canonical="/cards"
    />
);

export const WeddingWebsiteSEO = () => (
    <SEO
        title="Wedding Website Design Salem | Wedding Website Tamil Nadu | AMY Technologies"
        description="Professional wedding website design by AMY Technologies, Salem. Custom wedding websites with RSVP, gallery, countdown & venue map. Serving couples across Tamil Nadu."
        keywords="wedding website Salem, wedding website design Tamil Nadu, online wedding invitation Salem, AMY Technologies wedding website, digital wedding invitation Salem, custom wedding website Tamil Nadu, wedding website Vazhapadi"
        canonical="/weddingwebsite"
    />
);

export const AboutSEO = () => (
    <SEO
        title="About ASM Wedding Cards | Salem's Best Card Shop Since 2009"
        description="Learn about ASM Wedding Cards – Salem's trusted invitation card printers since 2009. Located in Vazhapadi, serving Salem, Erode & Tamil Nadu with premium quality cards."
        keywords="ASM wedding cards about, wedding card shop Salem, invitation cards Salem history, ASM graphics Salem, Vazhapadi wedding cards, best card shop Salem Tamil Nadu"
        canonical="/about"
    />
);

export const ContactSEO = () => (
    <SEO
        title="Contact ASM Wedding Cards | Wedding Card Shop Salem, Vazhapadi"
        description="Contact ASM Wedding Cards in Vazhapadi, Salem. Call +91 9865230900 or visit us at 75, Thammampatti Main Road, Vazhapadi, Salem District, Tamil Nadu 636115."
        keywords="ASM wedding cards contact, wedding card shop Salem address, invitation cards shop Vazhapadi, wedding cards Salem phone number, ASM cards Salem location"
        canonical="/contact"
    />
);

export default SEO;
